import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, NotificationModal, Tooltip } from '../../../shared/components/ui';
import { menuService } from '../services/menuService';
import RecipeSelectorModal from '../../meal/components/RecipeSelectorModal';
import { mealService } from '../../meal/services/mealService';
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para vista previa de cada meal con botón de repetible
const MealPreviewCard = ({ meal, mealType, onToggleRepeatable, menuMetadata, onAssignRecipe, isCurrentWeek = false }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isRepeatable, setIsRepeatable] = useState(meal.isRepeatable || false);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        setIsRepeatable(meal.isRepeatable || false);
    }, [meal.isRepeatable]);

    const handleToggle = async (e) => {
        e.stopPropagation();
        if (!meal.id || !onToggleRepeatable) return;

        setIsUpdating(true);
        try {
            // Optimistic update
            setIsRepeatable(!isRepeatable);
            await onToggleRepeatable(meal.id);
        } catch (error) {
            // Revertir en caso de error
            setIsRepeatable(isRepeatable);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="p-3 bg-white rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-1">{t(`meal.types.${meal.mealType || mealType}`) || mealType}</h4>
            <p className="text-sm text-gray-600 mb-2">{meal.name}</p>

            <div className="flex flex-col gap-1">
                {meal.id && onToggleRepeatable && (
                    <Tooltip
                        content={t('menu.display.repeatWeeklyTooltip')}
                        position="top"
                    >
                        <Button
                            variant={isRepeatable ? "primary" : "ghost"}
                            size="sm"
                            onClick={handleToggle}
                            disabled={isUpdating}
                            className={`w-full text-xs sm:text-sm min-h-[44px] ${isRepeatable
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'text-blue-600 hover:bg-blue-50 border border-blue-200'
                                }`}
                        >
                            {isUpdating ? (
                                <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-current"></span>
                            ) : (
                                <>
                                    <span className="mr-2">📅</span>
                                    {t('menu.display.repeatWeekly')}
                                </>
                            )}
                        </Button>
                    </Tooltip>
                )}

                {meal.id && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/recipe/${meal.recipe_id || meal.id}`);
                        }}
                        className="w-full text-xs sm:text-sm text-gray-600 hover:bg-gray-50 border border-gray-200 min-h-[44px]"
                    >
                        <span className="mr-2">👁️</span>
                        {t('menu.display.viewRecipe') || 'Ver receta'}
                    </Button>
                )}

                {/* Botón para seleccionar receta de biblioteca */}
                {meal.id && menuMetadata && (menuMetadata.status === 'draft' || isCurrentWeek) && onAssignRecipe && (
                    <Tooltip
                        content={t('menu.display.selectFromLibraryTooltip')}
                        position="top"
                        maxWidth="md"
                    >
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAssignRecipe(meal.id, meal.mealType);
                            }}
                            className="w-full text-xs sm:text-sm text-purple-600 hover:bg-purple-50 border border-purple-200 min-h-[44px]"
                        >
                            <span className="mr-2">🔄</span>
                            <span className="hidden sm:inline">{t('menu.display.selectFromLibrary')}</span><span className="sm:hidden">{t('menu.display.selectFromLibrary')}</span>
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

// Componente para cada card sortable
const SortableMenuItem = ({ menu, index, onMenuSelect, onToggleRepeatable, menuMetadata, onAssignRecipe, isPastDay = false, isExpanded = false, onToggleExpand, isCurrentWeek = false }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: menu.date || index });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Fecha no disponible';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getDayName = (dateString) => {
        if (!dateString) return `Día ${index + 1}`;
        const date = new Date(dateString);
        const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        return dayNames[date.getDay()];
    };

    // Función para formatear la fecha para la URL (YYYY-MM-DD)
    const formatDateForUrl = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
    };

    const menuData = menu.menu_data || menu;

    // Si es un día pasado y no está expandido, mostrar versión colapsada
    if (isPastDay && !isExpanded) {
        return (
            <div ref={setNodeRef} style={style}>
                <Card padding="none" className="p-3 sm:p-4 bg-gray-50 border-gray-200 hover:shadow-md transition-shadow">
                    <button
                        onClick={onToggleExpand}
                        className="w-full flex justify-between items-center gap-2 text-left"
                    >
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            <div className="min-w-0 max-w-[130px] sm:max-w-none sm:flex-1">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-800 truncate">
                                    {getDayName(menu.date)}
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-600 leading-tight">
                                    {formatDate(menu.date)}
                                </p>
                            </div>
                        </div>
                        <svg
                            className={`w-5 h-5 text-gray-600 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </Card>
            </div>
        );
    }

    return (
        <div ref={setNodeRef} style={style}>
            <Card padding="none" className="p-3 sm:p-6 bg-gray-50 border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4 gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div
                            {...attributes}
                            {...listeners}
                            className="cursor-grab active:cursor-grabbing p-2 hover:bg-gray-200 rounded transition-colors flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
                            title="Arrastra para reordenar"
                        >
                            <span className="text-xl sm:text-2xl">☰</span>
                        </div>
                        <div className="min-w-0 max-w-[130px] sm:max-w-none sm:flex-1">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-800 truncate">
                                {getDayName(menu.date)}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 leading-tight">
                                {formatDate(menu.date)}
                            </p>
                        </div>
                        {isPastDay && (
                            <button
                                onClick={onToggleExpand}
                                className="p-2 hover:bg-gray-200 rounded transition-colors flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                title="Ocultar día"
                            >
                                <svg
                                    className={`w-5 h-5 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        )}
                    </div>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            const dateUrl = formatDateForUrl(menu.date);
                            if (dateUrl) {
                                navigate(`/plan/${dateUrl}`);
                            } else {
                                // Fallback al comportamiento anterior si no hay fecha
                                onMenuSelect(menuData);
                            }
                        }}
                        className="min-h-[44px] text-sm whitespace-nowrap flex-shrink-0"
                    >
                        {t('menu.display.viewMenu') || 'Ver menú'}
                    </Button>
                </div>

                {/* Vista previa del menú */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3">
                    {menuData.breakfast && (
                        <MealPreviewCard
                            meal={menuData.breakfast}
                            mealType="Desayuno"
                            onToggleRepeatable={onToggleRepeatable}
                            menuMetadata={menuMetadata}
                            onAssignRecipe={onAssignRecipe}
                            isCurrentWeek={isCurrentWeek}
                        />
                    )}
                    {menuData.lunch && (
                        <MealPreviewCard
                            meal={menuData.lunch}
                            mealType="Comida"
                            onToggleRepeatable={onToggleRepeatable}
                            menuMetadata={menuMetadata}
                            onAssignRecipe={onAssignRecipe}
                            isCurrentWeek={isCurrentWeek}
                        />
                    )}
                    {menuData.dinner && (
                        <MealPreviewCard
                            meal={menuData.dinner}
                            mealType="Cena"
                            onToggleRepeatable={onToggleRepeatable}
                            menuMetadata={menuMetadata}
                            onAssignRecipe={onAssignRecipe}
                            isCurrentWeek={isCurrentWeek}
                        />
                    )}
                </div>
            </Card>
        </div>
    );
};

const SavedMenus = ({ onMenuSelect, menus = [], isLoading = false, onMenusReordered, onToggleRepeatable, menuMetadata = null, isCurrentWeek = false }) => {
    const { t } = useTranslation();
    const [localMenus, setLocalMenus] = useState([]);
    const [isReordering, setIsReordering] = useState(false);

    // Estados para RecipeSelectorModal
    const [isRecipeSelectorOpen, setIsRecipeSelectorOpen] = useState(false);
    const [selectedMealToReplace, setSelectedMealToReplace] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);

    // Estados para NotificationModal
    const [notification, setNotification] = useState({ isOpen: false, type: 'success', message: '' });

    // Estado para modal de confirmación de cambio de receta
    const [confirmChangeModal, setConfirmChangeModal] = useState({ isOpen: false, mealId: null, mealType: null });

    // Estado para días expandidos (solo para días pasados en semana en curso)
    const [expandedPastDays, setExpandedPastDays] = useState(new Set());

    // Función para verificar si un día ya pasó
    const isPastDay = (dateString) => {
        if (!dateString || !isCurrentWeek) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const menuDate = new Date(dateString);
        menuDate.setHours(0, 0, 0, 0);
        return menuDate < today;
    };

    // Función para toggle del estado expandido de un día pasado
    const togglePastDay = (dateString) => {
        if (!dateString) return;
        setExpandedPastDays(prev => {
            const newSet = new Set(prev);
            if (newSet.has(dateString)) {
                newSet.delete(dateString);
            } else {
                newSet.add(dateString);
            }
            return newSet;
        });
    };

    // Configurar sensores para drag & drop (incluyendo touch para mobile)
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handler para abrir el modal de selección de receta
    const handleOpenRecipeSelector = (mealId, mealType) => {
        // Si es semana en curso, mostrar modal de confirmación primero
        if (isCurrentWeek) {
            setConfirmChangeModal({ isOpen: true, mealId, mealType });
        } else {
            // Si no es semana en curso, abrir directamente el selector
            setSelectedMealToReplace({ id: mealId, type: mealType });
            setIsRecipeSelectorOpen(true);
        }
    };

    // Handler para confirmar el cambio de receta después del modal de confirmación
    const handleConfirmRecipeChange = () => {
        const { mealId, mealType } = confirmChangeModal;
        setConfirmChangeModal({ isOpen: false, mealId: null, mealType: null });
        setSelectedMealToReplace({ id: mealId, type: mealType });
        setIsRecipeSelectorOpen(true);
    };

    // Handler para asignar una receta seleccionada
    const handleRecipeSelected = async (recipeId) => {
        if (!selectedMealToReplace) return;

        setIsAssigning(true);

        try {
            const result = await mealService.assignRecipeToSlot(
                selectedMealToReplace.id,
                recipeId
            );

            if (result.success) {
                // Recargar los menús para reflejar el cambio
                if (onMenusReordered) {
                    await onMenusReordered();
                }

                setIsRecipeSelectorOpen(false);
                setSelectedMealToReplace(null);

                setNotification({ isOpen: true, type: 'success', message: 'Receta asignada correctamente' });
            }
        } catch (error) {
            setNotification({ isOpen: true, type: 'error', message: 'Error al asignar receta: ' + error.message });
        } finally {
            setIsAssigning(false);
        }
    };

    // Actualizar menús locales cuando cambien los props
    useEffect(() => {
        setLocalMenus(menus);
    }, [menus]);

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = localMenus.findIndex(m => (m.date || m.id) === active.id);
        const newIndex = localMenus.findIndex(m => (m.date || m.id) === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        // Actualizar el orden localmente (optimistic update)
        const reorderedMenus = arrayMove(localMenus, oldIndex, newIndex);
        setLocalMenus(reorderedMenus);

        // Calcular el mapeo de fechas
        const dateMapping = {};

        // Solo intercambiamos las fechas de los menús que se movieron
        const oldDate = localMenus[oldIndex].date;
        const newDate = localMenus[newIndex].date;

        if (oldDate && newDate) {
            // Intercambiar las fechas
            dateMapping[oldDate] = newDate;
            dateMapping[newDate] = oldDate;

            try {
                setIsReordering(true);

                // Llamar a la API para actualizar las fechas
                await menuService.reorderMenus(dateMapping);

                // Notificar al padre para que recargue los menús
                if (onMenusReordered) {
                    await onMenusReordered();
                }
            } catch (error) {
                // Revertir el cambio local en caso de error
                setLocalMenus(menus);
                setNotification({ isOpen: true, type: 'error', message: t('menu.errors.reorderFailed') });
            } finally {
                setIsReordering(false);
            }
        }
    };

    if (localMenus.length === 0) {
        return null;
    }

    return (
        <div className="max-w-4xl mx-auto">
            {isReordering && (
                <div className="mb-4 p-3 bg-blue-100 text-blue-800 rounded-lg text-center">
                    {t('common.updating')}
                </div>
            )}

            <div className="mb-4 text-xs sm:text-sm text-gray-600 text-center px-2">
                💡 <span className="hidden sm:inline">{t('menu.saved.dragHint')}</span><span className="sm:hidden">{t('menu.saved.holdHint')}</span>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={localMenus.map(m => m.date || m.id || Math.random())}
                    strategy={verticalListSortingStrategy}
                >
                    <div className="grid gap-4 sm:gap-6">
                        {localMenus.map((menu, index) => {
                            const isPast = isPastDay(menu.date);
                            const isExpanded = expandedPastDays.has(menu.date);
                            return (
                                <SortableMenuItem
                                    key={menu.date || menu.id || index}
                                    menu={menu}
                                    index={index}
                                    onMenuSelect={onMenuSelect}
                                    onToggleRepeatable={onToggleRepeatable}
                                    menuMetadata={menuMetadata}
                                    onAssignRecipe={handleOpenRecipeSelector}
                                    isPastDay={isPast}
                                    isExpanded={isExpanded}
                                    onToggleExpand={() => togglePastDay(menu.date)}
                                    isCurrentWeek={isCurrentWeek}
                                />
                            );
                        })}
                    </div>
                </SortableContext>
            </DndContext>

            {/* Modal de selección de receta */}
            <RecipeSelectorModal
                isOpen={isRecipeSelectorOpen}
                onClose={() => {
                    setIsRecipeSelectorOpen(false);
                    setSelectedMealToReplace(null);
                }}
                onSelectRecipe={handleRecipeSelected}
                mealType={selectedMealToReplace?.type}
            />

            {/* Modal de notificación */}
            <NotificationModal
                isOpen={notification.isOpen}
                onClose={() => setNotification({ ...notification, isOpen: false })}
                type={notification.type}
                message={notification.message}
            />

            {/* Modal de confirmación para cambio de receta en semana en curso */}
            <NotificationModal
                isOpen={confirmChangeModal.isOpen}
                onClose={() => setConfirmChangeModal({ isOpen: false, mealId: null, mealType: null })}
                onConfirm={handleConfirmRecipeChange}
                type="warning"
                title={t('menu.changeRecipe.confirmTitle') || 'Cambiar receta en semana en curso'}
                message={t('menu.changeRecipe.confirmMessage') || 'Si cambias la receta en la semana en curso, la lista de la compra no se recalculará automáticamente. ¿Deseas continuar?'}
                confirmText={t('common.confirm') || 'Continuar'}
                cancelText={t('common.cancel') || 'Cancelar'}
            />
        </div>
    );
};

export default SavedMenus;
